import { computed, ref } from 'vue';

export const JOINT_OPTION_CATEGORY_KEYS = {
    COVER: 'cover-improvements',
    GUIDE: 'guide-improvements',
} as const;

export type JointOptionCategoryKey = (typeof JOINT_OPTION_CATEGORY_KEYS)[keyof typeof JOINT_OPTION_CATEGORY_KEYS];

export interface JointOptionCategory {
    key: JointOptionCategoryKey;
    label: string;
}

/**
 * Step 5 state: additional joint options.
 *
 * Owns the catalogue of optional joint extras shown in `AdditionalOptions.vue`
 * (left side) and previewed in `OptionsPreview.vue` (right side). Each entry
 * acts as a checkbox — the user can select all, some or none, and the step is
 * always skippable.
 *
 * This data is NOT part of the existing checklist `formData`; it is shared
 * configurator data and is written back to the configured checklist PID when
 * the joint is added.
 */
export interface JointOption {
    /** Small icon shown inside the selectable box (left side). */
    icon: string;
    /** Larger image shown in the preview pane when the option is selected. */
    previewImage: string;
    /** Human-readable label shown above the icon (passed through i18n). */
    label: string;
    /** Short helper text shown below the icon (passed through i18n). */
    text: string;
    /** Selection flag — toggled by clicking the option box. */
    isSelected: boolean;
    /** Dynamic tab/category membership for the option selector. */
    categoryKey: JointOptionCategoryKey;
    key: string;
}

export interface JointOptionTab extends JointOptionCategory {
    options: JointOption[];
}

const JOINT_OPTION_CATEGORIES: JointOptionCategory[] = [
    {
        key: JOINT_OPTION_CATEGORY_KEYS.COVER,
        label: 'Cover improvements',
    },
    {
        key: JOINT_OPTION_CATEGORY_KEYS.GUIDE,
        label: 'Guide improvements',
    },
];

const optionOneIcon: string = require('@/modules/ConfiguratorJoints/assets/option-1-icon.png');
const optionOnePreview: string = require('@/modules/ConfiguratorJoints/assets/option-1-preview.png');
const optionTwoIcon: string = require('@/modules/ConfiguratorJoints/assets/option-2-icon.png');
const optionTwoPreview: string = require('@/modules/ConfiguratorJoints/assets/option-2-preview.png');
const optionThreeIcon: string = require('@/modules/ConfiguratorJoints/assets/option-3-icon.png');
const optionThreePreview: string = require('@/modules/ConfiguratorJoints/assets/option-3-preview.webp');

function buildDefaultOptions(availableOptions: string[], selectedOptionKeys: string[]): JointOption[] {
    return [
        {
            icon: optionOneIcon,
            previewImage: optionOnePreview,
            label: 'Closing corner joint cover with additional support profiles',
            text: 'Covers the top of the corner joint with a closed cap.',
            isSelected: selectedOptionKeys.includes('bottomCornerCover'),
            categoryKey: JOINT_OPTION_CATEGORY_KEYS.COVER,
            key: 'bottomCornerCover',
        },
        {
            icon: optionTwoIcon,
            previewImage: optionTwoPreview,
            label: 'Optional corner mask closure',
            text: 'Leaves the bottom of the corner joint open for drainage.',
            isSelected: selectedOptionKeys.includes('closingPlate'),
            categoryKey: JOINT_OPTION_CATEGORY_KEYS.GUIDE,
            key: 'closingPlate',
        },
        {
            icon: optionThreeIcon,
            previewImage: optionThreePreview,
            label: 'Corner joint element',
            text: 'Dedicated corner element that efficiently connects boxes and creates a precise corner joint.',
            isSelected: selectedOptionKeys.includes('olimpCornerElement'),
            categoryKey: JOINT_OPTION_CATEGORY_KEYS.COVER,
            key: 'olimpCornerElement',
        },
    ].filter((item) => availableOptions.includes(item.key));
}

export function useOptions(currentConfig: any) {
    const availableOptions = computed(() => {
        return currentConfig.value?.availableOptions ?? [];
    });
    const selectedOptionKeys = ref<string[]>([]);
    const options = computed<JointOption[]>(() => buildDefaultOptions(availableOptions.value, selectedOptionKeys.value));

    const optionTabs = computed<JointOptionTab[]>(() =>
        JOINT_OPTION_CATEGORIES.map((category) => ({
            ...category,
            options: options.value.filter((option) => option.categoryKey === category.key),
        })),
    );

    const selectedOptions = computed<JointOption[]>(() => options.value.filter((option) => option.isSelected));

    function toggleOption(key: string) {
        if (!options.value.some((entry) => entry.key === key)) {
            return;
        }
        if (selectedOptionKeys.value.includes(key)) {
            selectedOptionKeys.value = selectedOptionKeys.value.filter((selectedKey) => selectedKey !== key);
            return;
        }
        selectedOptionKeys.value = [...selectedOptionKeys.value, key];
    }

    function resetOptions() {
        selectedOptionKeys.value = [];
    }

    return {
        // State
        options,
        optionTabs,
        selectedOptions,
        // Mutations
        toggleOption,
        resetOptions,
    };
}
